export interface TraderConfig {
	fundId: string;
	strategyId: string;
	brokerId: string;
	brokerAccountId: string;
	allocation: number;
	maxLeverage: number;
	isEnabled?: boolean;
	minTradeThreshold?: number;
	ignoreMiscellaneousPositions?: boolean;
	useBrokerBuyingPower?: boolean;
	useOwnAccountEquity?: boolean;
	excludedSizingAccounts?: string[];
	excludeOwnedEquity?: boolean;
}

export interface SymbolOrderCap {
	symbol: string;
	// Optional AND nullable: a null nested value is dropped over HTTP (WhenWritingDefault), but
	// arrives as an explicit null from Redis-fed readers. Treat both as "no cap".
	maxQtyPerOrder?: number | null;
	maxNotionalPerOrder?: number | null;
}

export interface AccountConfig {
	fundId: string;
	note: string;
	brokerId: string;
	brokerAccountId: string; // identity within a broker (was optional)
	displayName?: string; // new
	isFractionalEnabled?: boolean;
	settings?: string;
	maxQtyPerOrder?: number | null;
	maxNotionalPerOrder?: number | null;
	targetSliceDelayMs?: number | null; // 0 is a VALUE (no pause); null/absent = Core.API's built-in 2000 ms
	symbolOrderCaps?: SymbolOrderCap[];
}

export interface FundConfig {
	fundId: string;
	displayName: string;
	legacyAccountId?: string;
	note?: string;
	isEnabled: boolean;
	showInPresentation?: boolean;
}

// String-wire C# enums (Common's ExecutionWindow.cs carries a type-level JsonStringEnumConverter),
// so string-literal unions, not TS enums. The zero members are EQUAL / SHARES / ALL.
export type ExecutionSplitRule = 'EQUAL' | 'CAPPED';
export type ExecutionSizeUnit = 'SHARES' | 'NOTIONAL';
export type ExecutionSymbolScope = 'ALL' | 'LISTED';

// Common's StrategyConfig.ExecutionWindow (Core.Trader#173). Every field is optional because
// Core.Config's HTTP reply (WhenWritingDefault) may drop enabled:false, every zero-member enum
// and every 0 — read absent as the C# default (windowFromStored does).
export interface ExecutionWindow {
	enabled?: boolean;
	cutoffSeconds?: number;
	trancheIntervalSeconds?: number;
	splitRule?: ExecutionSplitRule;
	sizeUnit?: ExecutionSizeUnit;
	trancheSize?: number;
	symbolScope?: ExecutionSymbolScope;
	symbols?: string[];
	// Core.Trader#181: 0–50, null/absent = off. The server 400s an out-of-range value even on an
	// OFF window; the UI's save path (windowToWire) carries the P1 fallback-to-null rule instead.
	variationPercent?: number | null;
}

export interface StrategyConfig {
	strategyFuncId: string;
	strategyId: string;
	note: string;
	inputs: string;
	lifecycle: string;
	displayName?: string;
	// Null or absent when unset — whether Core.Config's web-default reply writes an explicit null
	// or omits the key is unmeasured, so a consumer may see either. Null/absent schedule = no
	// upkeep; null/absent venue = NYSE. Deliberately NOT defaulted in CoreConfigApiService: a
	// boundary restore would write 'NYSE' onto every strategy on its next save.
	upkeepSchedule?: string | null;
	upkeepVenue?: string | null;
	// Null/absent = never stored (today's all-at-once rebalance). Off is { enabled: false, … }:
	// the server keeps the values so the switch is reversible. There is no way to clear it to null.
	executionWindow?: ExecutionWindow | null;
}

// TraderLifecycle.DAILY_AGGREGATE is ordinal 0 in Common/Constants/TraderLifecycle.cs, and
// Core.Config serializes with DefaultIgnoreCondition = WhenWritingDefault — so the key is
// ABSENT from the wire for every daily-aggregate strategy. Absent therefore means "the
// default", never "unknown"; normalize at the API boundary (CoreConfigApiService) so no
// consumer has to re-derive it.
export const DEFAULT_TRADER_LIFECYCLE = 'DAILY_AGGREGATE';

// User-configurable strategy funcs. Wire values must match StrategyFuncIds.Value() in
// Common/Constants/StrategyFuncIds.cs. Keys === values === the wire strings (not the C#
// member names) — strategyId derivation and funcId comparisons depend on this.
// Internal funcs (NTFN/DEV/EXAMPLE) are intentionally omitted from the admin dropdown.
export enum StrategyFuncId {
	OGD = 'OGD',
	SWB = 'SWB',
	SWBV2 = 'SWBV2',
	SWBB = 'SWBB',
	ARGO = 'ARGO',
	MOM = 'MOM',
	ARMP = 'ARMP',
	OASIS = 'OASIS',
	VOLA = 'VOLA',
	SIMPLE = 'SIMPLE',
	OMRP = 'OMRP',
	CHAIN = 'CHAIN',
	DCS = 'DCS'
}

export interface StrategySignalConnectionConfig {
	signalId: string;
	strategyId: string;
}

export interface ProviderSymbolOverride {
	ticker?: string;
	exchange?: string;
}

export interface ContactConfig {
	contactId: string;
	name: string;
	email: string;
	phone?: string;
}

export interface FundContactConfig {
	fundId: string;
	contactId: string;
	brokerId: string;
	canAuthorize: boolean;
	receivesReports: boolean;
}

// Read-side shape for GET /contacts. brokerId is null for fund-wide (non broker-specific) links —
// preserve null rather than coercing to '' so callers can distinguish "fund-wide" from "no broker set".
export interface ContactDTO {
	contactId: string;
	name: string;
	email: string;
	phone?: string;
	fundId: string;
	brokerId: string | null;
	canAuthorize: boolean;
	receivesReports: boolean;
}

export interface ContactPerson {
	contactId: string;
	name: string;
	email: string;
	phone?: string;
}

export interface SymbolConfig {
	partitionKey?: string;
	rowKey?: string;
	symbol: string;
	isEnabled: boolean;
	isIndex: boolean;
	isSubscription: boolean;
	isQuotePublished: boolean;
	priority: number;
	isSharia: boolean;
	isFutures: boolean;
	description?: string;
	alternateOptionRoot?: string;
	providerOverrides: string;
	futuresFallback?: string;
}
